import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, km, priceLabel } from "@/lib/api";

const SEEN = "po_waiting_seen";
const seen = () => JSON.parse(sessionStorage.getItem(SEEN) || "[]");

// Approved providers get a pop-up when a request of their profession is waiting for someone.
export default function WaitingWatcher() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [details, setDetails] = useState(false);
  const [closed, setClosed] = useState(seen);
  const enabled = user?.provider?.status === "approved" && !user.must_change_password;
  const { data = [] } = useQuery({ queryKey: ["waiting"], queryFn: () => api.get("/provider/waiting").then((r) => r.data), enabled, refetchInterval: 30000 });
  const req = enabled ? data.find((r) => !closed.includes(r.id)) : null;
  const close = () => {
    const next = [...closed, req.id];
    sessionStorage.setItem(SEEN, JSON.stringify(next));
    setClosed(next);
    setDetails(false);
  };
  const act = async (kind) => {
    try {
      if (kind === "accept") {
        await api.post(`/requests/${req.id}/accept`);
        toast.success("Pedido aceito! O cliente foi avisado.");
        close();
        navigate(`/pedido/${req.id}`);
      } else {
        await api.post(`/requests/${req.id}/waiting/decline`);
        toast.success("Pedido recusado.");
        close();
      }
    } catch (e) {
      toast.error(errMsg(e));
      close();
    }
    qc.invalidateQueries({ queryKey: ["waiting"] });
  };
  if (!req) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md rounded-3xl" data-testid="waiting-dialog">
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-orange-100 text-orange-600"><BellRing className="h-9 w-9" /></span>
        <DialogTitle className="text-center font-display text-2xl font-extrabold" data-testid="waiting-title">Tem um pedido de {req.service_name} aguardando</DialogTitle>
        {!details ? (
          <>
            <DialogDescription className="text-center text-lg text-slate-700">Ninguém pegou ainda. Veja se você pode atender.</DialogDescription>
            <button onClick={() => setDetails(true)} className="po-btn-primary min-h-[60px] text-lg" data-testid="waiting-view">Ver pedido</button>
            <button onClick={close} className="po-btn-outline min-h-[52px] text-lg" data-testid="waiting-later">Depois</button>
          </>
        ) : (
          <>
            <DialogDescription asChild>
              <div className="space-y-1 rounded-2xl bg-orange-50 p-4 text-left text-lg text-slate-800" data-testid="waiting-details">
                <p><b>{req.neighborhood}, {req.city}</b>{req.distance_km != null && ` · ${km(req.distance_km)} de você`}</p>
                <p>{priceLabel(req)}</p>
                {req.description && <p className="text-base text-slate-700">{req.description}</p>}
                <p className="text-sm text-slate-500">Pedido feito em {new Date(req.created_at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}</p>
              </div>
            </DialogDescription>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => act("accept")} className="po-btn-green min-h-[60px] text-lg" data-testid="waiting-accept"><Check className="h-6 w-6" /> Aceitar</button>
              <button onClick={() => act("decline")} className="po-btn-outline min-h-[60px] text-lg" data-testid="waiting-decline"><X className="h-6 w-6" /> Recusar</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
